/**
 * Theme Provider Component - React Context for theme management
 *
 * Following ReactJS standards:
 * - Functional component with hooks
 * - React Context for state sharing
 * - Custom hooks for theme management
 * - TypeScript interfaces
 * - Performance optimized with React.memo
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import type { ThemeContextValue } from '@/types/theme';
import { createLightTheme, createDarkTheme } from '@/styles/mintytheme';

import { ThemeContext } from './ThemeContext';

// Local storage key for theme persistence
const THEME_STORAGE_KEY = 'budgetspeech-theme-mode';

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * ThemeProvider - Provides theme context to the entire application
 *
 * Features:
 * - Light/dark mode toggle
 * - Persistence to localStorage
 * - Mintytheme integration
 * - React Context pattern
 *
 * @param props - Component props
 * @returns JSX.Element
 */
export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  // Initialize theme mode from localStorage or default to light
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false; // Default to light mode on server
    }

    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) {
      return stored === 'dark';
    }

    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Create theme based on current mode
  const theme = useMemo(() => {
    return isDarkMode ? createDarkTheme() : createLightTheme();
  }, [isDarkMode]);

  // Toggle theme function
  const toggleTheme = useCallback(() => {
    setIsDarkMode(prev => {
      const newMode = !prev;
      // Persist to localStorage
      localStorage.setItem(THEME_STORAGE_KEY, newMode ? 'dark' : 'light');
      return newMode;
    });
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if no preference is stored
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (!stored) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      theme,
      isDarkMode,
      toggleTheme,
    }),
    [theme, isDarkMode, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Export theme provider as default for convenience
export default ThemeProvider;
